import { mrbSnBul } from "@oasis/domain";
import { bicimDili, cevir, dil, type Dil } from "@/lib/dil";

/**
 * D-060: MRB toplu yükleme penceresinin ve dönüştürücü durumunun saf mantığı. Ekran (Mrb.tsx, MrbDetay.tsx,
 * MrbTopluYukleme.tsx) yalnız çizer; eşleme, satır durumları, sunucu yanıtının satır sonucuna çevrilmesi,
 * sıradaki satırın seçimi ve gösterilecek metinler burada. JSX yok; React ve oturum katmanını yüklemez.
 * Sunucu metinleri İngilizce gelir, ekranda `cevir(metin)` ile gösterilir (lib/api.ts'teki gibi).
 */

export type Ton = "neutral" | "info" | "ok" | "warn" | "bad";

/* ------------------------------------------------------------ biçim ------------------------------------------------------------ */

/** Saat: "14:20". */
export const saat = (v: string | Date) => new Date(v).toLocaleTimeString(bicimDili(), { hour: "2-digit", minute: "2-digit" });
/** Kısa tarih ve saat (yıl yok): "08/10, 14:12". */
export const kisaTarihSaat = (v: string | Date) => new Date(v).toLocaleString(bicimDili(), { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
/** Kısa tarih (yıl yok): "08/10". */
export const kisaTarih = (v: string | Date) => new Date(v).toLocaleDateString(bicimDili(), { day: "2-digit", month: "2-digit" });

/** Sıra numarası: İngilizcede 1st · 2nd · 3rd · 11th, Türkçede 1. · 2. */
export function sirali(n: number, d: Dil = dil()): string {
  if (d === "tr") return `${n}.`;
  const son2 = n % 100;
  const son = n % 10;
  const ek = son2 >= 11 && son2 <= 13 ? "th" : son === 1 ? "st" : son === 2 ? "nd" : son === 3 ? "rd" : "th";
  return `${n}${ek}`;
}

/** Süre: "6 min" · "3 h 10 min" · "2 d 4 h" (dakikaya yuvarlanır, en az 1 dakika). */
export function sureMetni(ms: number): string {
  const dk = Math.max(1, Math.floor(ms / 60_000));
  if (dk < 60) return cevir("{0} min", dk);
  const sa = Math.floor(dk / 60);
  if (sa < 24) return dk % 60 ? cevir("{0} h {1} min", sa, dk % 60) : cevir("{0} h", sa);
  const gun = Math.floor(sa / 24);
  return sa % 24 ? cevir("{0} d {1} h", gun, sa % 24) : cevir("{0} d", gun);
}

/** "6 min ago"; bir dakikadan kısası "just now". */
export const gecenMetni = (ms: number): string => (ms < 60_000 ? cevir("just now") : cevir("{0} ago", sureMetni(ms)));

/* ------------------------------------------------- dönüşüm durumu (MRB inventory) ------------------------------------------------- */

/** Sunucudaki `MrbKutuk.donusum`: null = yok. */
export type Donusum = "sirada" | "donusturuluyor" | "hata";
/** "Content excel" kolonunun ve süzgecin durumu. */
export type IcerikDurumu = "yuklendi" | "donusturuluyor" | "sirada" | "hata" | "yok";
export const ICERIK_DURUMLARI: readonly IcerikDurumu[] = ["yuklendi", "donusturuluyor", "sirada", "hata", "yok"];

/** İçeriği olan MRB her zaman "uploaded" görünür; dönüşüm alanı gösterilmez (D-060). */
export function icerikDurumu(m: { icerik: object | null; donusum: string | null }): IcerikDurumu {
  if (m.icerik) return "yuklendi";
  if (m.donusum === "donusturuluyor" || m.donusum === "sirada" || m.donusum === "hata") return m.donusum;
  return "yok";
}

export function durumAdi(d: IcerikDurumu): string {
  switch (d) {
    case "yuklendi": return cevir("uploaded");
    case "donusturuluyor": return cevir("converting");
    case "sirada": return cevir("queued");
    case "hata": return cevir("failed");
    case "yok": return cevir("none");
  }
}

export const DURUM_TONU: Record<IcerikDurumu, Ton> = { yuklendi: "ok", donusturuluyor: "info", sirada: "neutral", hata: "bad", yok: "warn" };

export function durumSayilari(liste: readonly { icerik: object | null; donusum: string | null }[]): Record<IcerikDurumu, number> {
  const s: Record<IcerikDurumu, number> = { yuklendi: 0, donusturuluyor: 0, sirada: 0, hata: 0, yok: 0 };
  for (const m of liste) s[icerikDurumu(m)]++;
  return s;
}

/** "Yüklendi 3" gibi süzgeç çipleri için: ilk harf büyük (dilin kuralıyla; "iptal" → "İptal"). */
export const basHarfBuyuk = (s: string) => s.charAt(0).toLocaleUpperCase(bicimDili()) + s.slice(1);

/** Dönüştürücü bu süreden uzun bağlanmadıysa "not connected for …"; kuyrukta iş varsa sarı uyarı (D-060 varsayımı: 1 saat). */
export const BAGLANTI_ESIGI_MS = 60 * 60_000;

export interface DonusturucuRozeti {
  tur: "bagli" | "kopuk" | "hic";
  /** Son bağlantıdan bu yana geçen süre (ms); hiç bağlanmadıysa null. */
  gecen: number | null;
  /** Bağlantı eşikten eski ve sırada iş var: bilgisayar kapalı ya da uykuda olabilir. */
  uyari: boolean;
}

export function donusturucuRozeti(d: { sonBaglanti: string | Date | null; sirada: number }, simdi: number): DonusturucuRozeti {
  if (!d.sonBaglanti) return { tur: "hic", gecen: null, uyari: false };
  const gecen = Math.max(0, simdi - new Date(d.sonBaglanti).getTime());
  if (gecen <= BAGLANTI_ESIGI_MS) return { tur: "bagli", gecen, uyari: false };
  return { tur: "kopuk", gecen, uyari: d.sirada > 0 };
}

/**
 * Liste yenilemesi: her çağrı bir istek başlatır; yanıt yalnız o istek hâlâ en son başlatılansa yazılır. 30 sn'lik arka plan
 * yenilemesi ile bir işlemden sonraki yenileme aynı anda uçarken geç dönen eski yanıt, işlemin sonucunu gösteren yanıtı ezmesin.
 */
export function siraliYenile<T>(getir: () => Promise<T>, yaz: (v: T) => void): () => Promise<void> {
  let son = 0;
  return async () => {
    const n = ++son;
    const v = await getir();
    if (n === son) yaz(v);
  };
}

/* ------------------------------------------------------- toplu yükleme ------------------------------------------------------- */

/** Tarayıcıdaki `File`; testte yalnız adı olan nesne yeter. */
export interface DosyaBenzeri {
  name: string;
}

/**
 * bekliyor: gönderilecek · yukleniyor: istek sürüyor · tamam: sunucu kabul etti (sonuc dolu) ·
 * sn_gerekli: SN okunamadı, satırda girilir · pdf_gerekli: tek başına excel, buradan gönderilmez · hata: sunucu ya da ağ hatası.
 */
export type SatirDurumu = "bekliyor" | "yukleniyor" | "tamam" | "sn_gerekli" | "pdf_gerekli" | "hata";

export interface IcerikOzeti {
  model?: string;
  piece: number;
  agac: number;
  kimlik: number;
  karar: number;
  ck: number;
}

/** `POST /api/mrb/yukle` yanıtının bu pencereyi ilgilendiren alanları (sözleşme U-050). */
export interface YukleYaniti {
  hata?: string;
  hataKaynagi?: "sn" | "icerik" | null;
  sn?: string;
  duplike?: boolean;
  pdfDegismedi?: boolean;
  icerikOzet?: IcerikOzeti | null;
  icerikDegismedi?: boolean;
  kuyrugaGirdi?: boolean;
  siraNo?: number | null;
  icerikUyarilari?: string[];
}

export interface SatirSonucu {
  /** Kaydın SN'i (sunucunun okuduğu; yoksa satırınki). */
  mrbSn: string | null;
  duplike: boolean;
  pdfDegismedi: boolean;
  icerikOzet: IcerikOzeti | null;
  icerikDegismedi: boolean;
  kuyrugaGirdi: boolean;
  siraNo: number | null;
  uyarilar: string[];
}

export interface SatirHatasi {
  /** Sunucunun İngilizce metni (gösterimde çevrilir) ya da bu modülün metni. */
  metin: string;
  kaynak: "sn" | "icerik" | null;
  /** Ağ hatası ya da sunucu arızası (5xx): aynı satır olduğu gibi yeniden denenebilir. */
  tekrarlanabilir: boolean;
}

export interface TopluSatir<D extends DosyaBenzeri = File> {
  id: number;
  pdf: D | null;
  excel: D | null;
  /** Dosya adından okunan SN (eşleme bununla yapılır). */
  sn: string | null;
  /** Kullanıcının satırda girdiği SN; sunucuya yalnız bu gider (dosya adındakini sunucu kendisi okur). */
  girilenSn: string | null;
  durum: SatirDurumu;
  sonuc: SatirSonucu | null;
  hata: SatirHatasi | null;
}

export function dosyaTuru(ad: string): "pdf" | "excel" | null {
  if (/\.pdf$/i.test(ad)) return "pdf";
  if (/\.xlsx$/i.test(ad)) return "excel";
  return null;
}

/** Eşlemede kullanılan SN: satırda girilen geçerliyse o, yoksa dosya adındaki. */
export const etkinSn = (s: Pick<TopluSatir<DosyaBenzeri>, "sn" | "girilenSn">): string | null => (s.girilenSn ? mrbSnBul(s.girilenSn) : null) ?? s.sn;

function yeniSatir<D extends DosyaBenzeri>(id: number, pdf: D | null, excel: D | null, sn: string | null, durum: SatirDurumu): TopluSatir<D> {
  return { id, pdf, excel, sn, girilenSn: null, durum, sonuc: null, hata: null };
}

/**
 * Seçilen ya da sürüklenen dosyaları satırlara ekler. Önce PDF'ler, sonra excel'ler işlenir ki aynı seçimde sıra fark etmesin.
 * - PDF: aynı SN'de PDF'i bekleyen tek başına excel satırı varsa ona girer; yoksa yeni satır. SN okunamazsa "sn_gerekli".
 *   Aynı SN'de ikinci PDF ayrı satır olur ve sunucu sonucunu alır (ör. "same file already in system").
 * - Excel: aynı SN'de excel'i olmayan, henüz gönderilmemiş PDF satırına girer; yoksa "pdf_gerekli" satırı (gönderilmez).
 * PDF ve .xlsx dışındaki dosyalar eklenmez, adları `atlanan`da döner.
 */
export function dosyalariEkle<D extends DosyaBenzeri>(satirlar: readonly TopluSatir<D>[], dosyalar: readonly D[]): { satirlar: TopluSatir<D>[]; atlanan: string[] } {
  const sonuc = satirlar.map((s) => ({ ...s }));
  let id = sonuc.reduce((m, s) => Math.max(m, s.id), 0);
  const atlanan = dosyalar.filter((f) => !dosyaTuru(f.name)).map((f) => f.name);

  for (const f of dosyalar.filter((x) => dosyaTuru(x.name) === "pdf")) {
    const sn = mrbSnBul(f.name);
    const bekleyen = sn ? sonuc.find((s) => s.durum === "pdf_gerekli" && !s.pdf && s.excel && s.sn === sn) : undefined;
    if (bekleyen) {
      bekleyen.pdf = f;
      bekleyen.durum = "bekliyor";
      continue;
    }
    sonuc.push(yeniSatir(++id, f, null, sn, sn ? "bekliyor" : "sn_gerekli"));
  }

  for (const f of dosyalar.filter((x) => dosyaTuru(x.name) === "excel")) {
    const sn = mrbSnBul(f.name);
    const pdfSatiri = sn ? sonuc.find((s) => s.durum === "bekliyor" && s.pdf && !s.excel && etkinSn(s) === sn) : undefined;
    if (pdfSatiri) {
      pdfSatiri.excel = f;
      continue;
    }
    sonuc.push(yeniSatir(++id, null, f, sn, "pdf_gerekli"));
  }
  return { satirlar: sonuc, atlanan };
}

/**
 * SN okunamayan satıra girilen SN ("Retry"): satır yeniden gönderilmek üzere bekler. Satırın excel'i yoksa ve aynı SN'de
 * PDF'ini bekleyen tek başına excel satırı varsa o excel bu satıra taşınır, o satır kalkar.
 */
export function snGir<D extends DosyaBenzeri>(satirlar: readonly TopluSatir<D>[], id: number, girilen: string): TopluSatir<D>[] {
  const g = girilen.trim();
  const hedef = satirlar.find((s) => s.id === id);
  if (!hedef || !g || !hedef.pdf) return [...satirlar];
  const sn = mrbSnBul(g);
  const excelSatiri = !hedef.excel && sn ? satirlar.find((s) => s.id !== id && s.durum === "pdf_gerekli" && !s.pdf && s.excel && s.sn === sn) : undefined;
  return satirlar
    .filter((s) => s !== excelSatiri)
    .map((s) => (s.id === id ? { ...s, girilenSn: g, excel: s.excel ?? excelSatiri?.excel ?? null, durum: "bekliyor" as const, sonuc: null, hata: null } : s));
}

/** Satırı olduğu gibi yeniden kuyruğa koyar (ağ hatası ya da sunucu arızasından sonra "Retry"). */
export function tekrarDene<D extends DosyaBenzeri>(satirlar: readonly TopluSatir<D>[], id: number): TopluSatir<D>[] {
  return satirlar.map((s) => (s.id === id && s.pdf ? { ...s, durum: "bekliyor" as const, sonuc: null, hata: null } : s));
}

/** Excel'i reddedilen satır: excel satırdan çıkar, PDF tek başına yeniden gönderilir ("Retry without excel"). */
export function excelsizDene<D extends DosyaBenzeri>(satirlar: readonly TopluSatir<D>[], id: number): TopluSatir<D>[] {
  return satirlar.map((s) => (s.id === id && s.pdf ? { ...s, excel: null, durum: "bekliyor" as const, sonuc: null, hata: null } : s));
}

/**
 * Gönderilecek sıradaki satır: listede en üstteki bekleyen. `secim` null ise ("Upload" turu) bütün bekleyenler; yükleme sürerken
 * eklenen ya da yeniden denenen satırlar da sıraya girer. Küme ise yalnız içindeki satırlar ("Stop after this file"ten ya da tur
 * bittikten sonra satır düğmesiyle yeniden denenenler); öteki bekleyenler "Upload" denmedikçe gönderilmez.
 */
export function siradaki<D extends DosyaBenzeri>(satirlar: readonly TopluSatir<D>[], secim: ReadonlySet<number> | null = null): TopluSatir<D> | null {
  return satirlar.find((s) => s.durum === "bekliyor" && s.pdf && (!secim || secim.has(s.id))) ?? null;
}

/** Satır düğmesinden sonra seçim: "Upload" turu sürüyorsa (null) satır zaten o tura girer; yoksa seçime yalnız bu satır eklenir. */
export const secimeEkle = (secim: ReadonlySet<number> | null, id: number): Set<number> | null => (secim ? new Set(secim).add(id) : null);

/** Satırın isteği: `dosyaYukle("/mrb/yukle", pdf, alanlar, ekDosyalar)`. */
export function istekParcalari<D extends DosyaBenzeri>(s: TopluSatir<D>): { pdf: D; alanlar: Record<string, string>; ekDosyalar: Record<string, D> } {
  if (!s.pdf) throw new Error("PDF yok: bu satır gönderilmez.");
  return {
    pdf: s.pdf,
    alanlar: { toplu: "1", ...(s.girilenSn ? { sn: s.girilenSn } : {}) },
    ekDosyalar: s.excel ? { icerik: s.excel } : {},
  };
}

/** Gönderimin ham sonucu: 2xx gövdesi · 4xx/5xx (gövdesiyle) · ağ hatası. */
export type Gonderim =
  | { tur: "yanit"; govde: YukleYaniti }
  | { tur: "hata"; status: number; govde: unknown; mesaj: string }
  | { tur: "ag" };

export const AG_HATASI = "Could not reach the server; check the connection and retry.";

function hataSonucu(metin: string, kaynak: "sn" | "icerik" | null | undefined, tekrarlanabilir: boolean): Pick<TopluSatir, "durum" | "sonuc" | "hata"> {
  const k = kaynak === "sn" || kaynak === "icerik" ? kaynak : null;
  return { durum: k === "sn" ? "sn_gerekli" : "hata", sonuc: null, hata: { metin, kaynak: k, tekrarlanabilir } };
}

/**
 * Sunucu yanıtını satır sonucuna çevirir. Sunucu hatayı iki yoldan söyleyebilir (U-050): 200 gövdesinde `hata` ya da 4xx gövdesinde
 * `{hata, hataKaynagi}`. `hataKaynagi: "sn"` → SN girişi; `"icerik"` → "Retry without excel"; ağ hatası ve 5xx → "Retry".
 */
export function sonucuIsle(g: Gonderim, satirSn: string | null = null): Pick<TopluSatir, "durum" | "sonuc" | "hata"> {
  if (g.tur === "ag") return hataSonucu(AG_HATASI, null, true);
  if (g.tur === "hata") {
    const govde = (g.govde && typeof g.govde === "object" ? g.govde : {}) as { hata?: unknown; message?: unknown; hataKaynagi?: "sn" | "icerik" | null };
    const metin = typeof govde.hata === "string" ? govde.hata : typeof govde.message === "string" ? govde.message : g.mesaj;
    return hataSonucu(metin, govde.hataKaynagi, g.status >= 500);
  }
  const y = g.govde;
  if (y.hata) return hataSonucu(y.hata, y.hataKaynagi, false);
  return {
    durum: "tamam",
    hata: null,
    sonuc: {
      mrbSn: y.sn ?? satirSn,
      duplike: Boolean(y.duplike),
      pdfDegismedi: Boolean(y.pdfDegismedi),
      icerikOzet: y.icerikOzet ?? null,
      icerikDegismedi: Boolean(y.icerikDegismedi),
      kuyrugaGirdi: Boolean(y.kuyrugaGirdi),
      siraNo: y.siraNo ?? null,
      uyarilar: y.icerikUyarilari ?? [],
    },
  };
}

/** Satır, sonucu tek PDF'in yeni kaydedildiği ya da excel'inin bağlandığı bir yükleme mi ("uploaded" sayılır). */
const kaydedildi = (s: SatirSonucu) => (!s.duplike && !s.pdfDegismedi) || Boolean(s.icerikOzet);

export interface SatirGorunumu {
  etiketler: { metin: string; ton: Ton }[];
  aciklama: { metin: string; ton: "ink-2" | "ink-3" | "bad" } | null;
  /** Sunucunun notları (SN farkı, riser eşlemesi, excel'deki Assembly SN farkı): satır altında sarı. */
  uyarilar: string[];
  /** SN hücresinde giriş ve "Retry". */
  snGirisi: boolean;
  dugme: "tekrar" | "excelsiz" | null;
}

/** Satırın "Result" hücresi (tasarım: mrb--toplu-yukleme). */
export function satirGorunumu<D extends DosyaBenzeri>(satir: TopluSatir<D>): SatirGorunumu {
  const bos: SatirGorunumu = { etiketler: [], aciklama: null, uyarilar: [], snGirisi: false, dugme: null };
  switch (satir.durum) {
    case "bekliyor":
      return { ...bos, etiketler: [{ metin: cevir("waiting"), ton: "neutral" }] };
    case "yukleniyor":
      return { ...bos, etiketler: [{ metin: cevir("uploading…"), ton: "info" }], aciklama: { metin: cevir("Large MRBs can take up to a minute."), ton: "ink-3" } };
    case "pdf_gerekli":
      return { ...bos, etiketler: [{ metin: cevir("needs its PDF"), ton: "warn" }], aciklama: { metin: cevir("An excel alone is not sent from here. Add its PDF to this window, or upload the excel on the MRB record page."), ton: "ink-2" } };
    case "sn_gerekli":
      return {
        ...bos,
        etiketler: [{ metin: cevir("SN could not be read"), ton: "warn" }],
        aciklama: { metin: satir.hata ? cevir(satir.hata.metin) : cevir("The file name has no SN. Enter it and retry this row."), ton: "ink-2" },
        snGirisi: true,
      };
    case "hata": {
      const h = satir.hata;
      const icerik = h?.kaynak === "icerik";
      const metin = !h ? cevir("Upload failed") : icerik ? cevir("Content excel rejected: {0} The PDF was not saved either.", cevir(h.metin)) : cevir(h.metin);
      // Elle girilen SN'le eşlenen excel'in içindeki SN farklıysa sunucu kaynaksız hata verebilir: satır excel'siz denenebilsin.
      const excelsiz = icerik || Boolean(h && !h.kaynak && !h.tekrarlanabilir && satir.excel && satir.girilenSn);
      return { ...bos, etiketler: [{ metin: cevir("failed"), ton: "bad" }], aciklama: { metin, ton: "bad" }, dugme: excelsiz ? "excelsiz" : h?.tekrarlanabilir ? "tekrar" : null };
    }
    case "tamam": {
      const s = satir.sonuc;
      if (!s) return { ...bos, etiketler: [{ metin: cevir("uploaded"), ton: "ok" }] };
      const yeniPdf = !s.duplike && !s.pdfDegismedi;
      const mrb = s.mrbSn ?? etkinSn(satir) ?? "—";
      const etiketler: SatirGorunumu["etiketler"] = [];
      const cumleler: string[] = [];
      if (yeniPdf) etiketler.push({ metin: cevir("uploaded"), ton: "ok" });
      else if (s.pdfDegismedi) {
        etiketler.push({ metin: cevir("PDF not replaced"), ton: "warn" });
        cumleler.push(cevir("MRB {0} already has a different PDF; it was kept (to replace it, use \"Replace PDF\" on the record page).", mrb));
      } else {
        etiketler.push({ metin: cevir("same file already in system"), ton: "neutral" });
        // Excel'li satırda excel'in sonucu ayrıca yazılır; "nothing saved" yalnız tek başına PDF için doğru.
        if (!satir.excel) cumleler.push(cevir("SHA matched MRB {0}; nothing saved.", mrb));
      }
      if (s.icerikOzet) {
        const o = s.icerikOzet;
        if (yeniPdf) cumleler.push(cevir("Content excel attached: {0} identity · {1} flagged conflict · {2} C&K row · {3} document · {4} tree row. Not queued.", o.kimlik, o.karar, o.ck, o.piece, o.agac));
        else {
          etiketler.push({ metin: cevir("content excel attached"), ton: "ok" });
          cumleler.push(cevir("The MRB had no content excel, so this excel was attached and the MRB left the converter queue."));
        }
      }
      if (s.icerikDegismedi) {
        etiketler.push({ metin: cevir("content excel not changed"), ton: "warn" });
        cumleler.push(cevir("MRB {0} already has a content excel; the new one was not attached. To replace it, use the record page.", mrb));
      }
      if (s.kuyrugaGirdi) cumleler.push(s.siraNo ? cevir("No content excel: added to the converter queue ({0}).", sirali(s.siraNo)) : cevir("No content excel: added to the converter queue."));
      return { ...bos, etiketler, aciklama: cumleler.length ? { metin: cumleler.join(" "), ton: "ink-2" } : null, uyarilar: s.uyarilar.map((u) => cevir(u)) };
    }
  }
}

export interface TopluOzet {
  toplam: number;
  /** Sonucu belli satırlar (sırada ve yüklenmekte olanlar dışındakiler). */
  biten: number;
  yuklendi: number;
  ayniDosya: number;
  pdfDegismedi: number;
  snGerekli: number;
  pdfGerekli: number;
  hata: number;
  yukleniyor: number;
  bekliyor: number;
}

/** Pencerenin ilerleme satırı: "8 of 11 done" ve durum sayaçları. */
export function topluOzet<D extends DosyaBenzeri>(satirlar: readonly TopluSatir<D>[]): TopluOzet {
  const o: TopluOzet = { toplam: satirlar.length, biten: 0, yuklendi: 0, ayniDosya: 0, pdfDegismedi: 0, snGerekli: 0, pdfGerekli: 0, hata: 0, yukleniyor: 0, bekliyor: 0 };
  for (const s of satirlar) {
    if (s.durum === "bekliyor") o.bekliyor++;
    else if (s.durum === "yukleniyor") o.yukleniyor++;
    else if (s.durum === "sn_gerekli") o.snGerekli++;
    else if (s.durum === "pdf_gerekli") o.pdfGerekli++;
    else if (s.durum === "hata") o.hata++;
    else if (!s.sonuc || kaydedildi(s.sonuc)) o.yuklendi++;
    else if (s.sonuc.pdfDegismedi) o.pdfDegismedi++;
    else o.ayniDosya++;
  }
  o.biten = o.toplam - o.bekliyor - o.yukleniyor;
  return o;
}
